import React, { useState, useMemo, useCallback } from 'react';
import {
  Grid, Card, CardContent, Typography, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, FormControl, InputLabel, Select, MenuItem,
  TablePagination, Box, Chip, CircularProgress, Button,
  IconButton, Tooltip, InputAdornment
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Search, SwapHoriz, TrendingUp, TrendingDown, AllInclusive,
  Today, FilterList, PictureAsPdf, Refresh, BarChart
} from '@material-ui/icons';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  header: {
    marginBottom: theme.spacing(4),
  },
  statCard: {
    height: '100%',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
    },
  },
  statCardEntree: {
    borderTop: `4px solid ${theme.palette.success.main}`,
    background: 'linear-gradient(135deg, #f8fff8 0%, #e8f5e8 100%)',
  },
  statCardSortie: {
    borderTop: `4px solid ${theme.palette.error.main}`,
    background: 'linear-gradient(135deg, #fff8f8 0%, #f5e8e8 100%)',
  },
  statCardTotal: {
    borderTop: `4px solid ${theme.palette.primary.main}`,
    background: 'linear-gradient(135deg, #f8fdff 0%, #e8f2f5 100%)',
  },
  statIcon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.8,
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    margin: theme.spacing(1, 0),
  },
  statLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  searchSection: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    boxShadow: theme.shadows[2],
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    '& th': {
      color: 'white',
      fontWeight: 'bold',
      fontSize: '14px',
      padding: theme.spacing(2),
    },
  },
  tableRow: {
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  positive: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
    fontSize: '16px',
  },
  negative: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
    fontSize: '16px',
  },
  neutral: {
    color: theme.palette.warning.main,
    fontWeight: 'bold',
    fontSize: '16px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  todayBadge: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
    marginLeft: theme.spacing(1),
  },
  exportButton: {
    background: 'linear-gradient(45deg, #ff6b6b 30%, #ff8e53 90%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(45deg, #ff5252 30%, #ff7b42 90%)',
    },
  },
  typeBadge: {
    fontWeight: 'bold',
    fontSize: '12px',
    minWidth: 100,
  },
}));

const MouvementsStock = ({ mouvementsData = [], loading, onRefresh }) => {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('TOUS');
  const [dateFilter, setDateFilter] = useState('TOUTES');
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
  });

  const safeMouvementsData = Array.isArray(mouvementsData) ? mouvementsData : [];

  // Statistiques détaillées avec calculs avancés
  const { stats, mouvementsParType, entreesParDate } = useMemo(() => {
    if (!safeMouvementsData.length) {
      return {
        stats: { total: 0, entrees: 0, sorties: 0, quantiteTotale: 0 },
        mouvementsParType: new Map(),
        entreesParDate: new Map(),
      };
    }

    const mouvementsParTypeMap = new Map();
    const entreesParDateMap = new Map();
    
    const stats = safeMouvementsData.reduce((acc, mouvement) => {
      const quantite = parseInt(mouvement.quantite || 0);
      const date = new Date(mouvement.date_mouvement).toLocaleDateString('fr-FR');

      acc.total++;
      acc.quantiteTotale += Math.abs(quantite);

      if (quantite > 0) {
        acc.entrees++;
        // Calcul des entrées par date
        const current = entreesParDateMap.get(date) || 0;
        entreesParDateMap.set(date, current + quantite);
      } else {
        acc.sorties++;
      }

      // Calcul par type
      const typeStats = mouvementsParTypeMap.get(mouvement.type_mouvement) || { count: 0, quantite: 0 };
      typeStats.count++;
      typeStats.quantite += Math.abs(quantite);
      mouvementsParTypeMap.set(mouvement.type_mouvement, typeStats);

      return acc;
    }, {
      total: 0,
      entrees: 0,
      sorties: 0,
      quantiteTotale: 0,
    });

    return {
      stats,
      mouvementsParType: mouvementsParTypeMap,
      entreesParDate: entreesParDateMap,
    };
  }, [safeMouvementsData]);

  // Filtrage des données avec filtre par date
  const filteredData = useMemo(() => {
    if (!safeMouvementsData.length) return [];

    const aujourdhui = new Date().toLocaleDateString('fr-FR');

    return safeMouvementsData.filter(mouvement => {
      // Filtre par recherche
      const matchesSearch = searchTerm === '' ||
        mouvement.nomProd?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mouvement.motif?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mouvement.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mouvement.speudo?.toLowerCase().includes(searchTerm.toLowerCase());

      // Filtre par type
      const matchesType = typeFilter === 'TOUS' || mouvement.type_mouvement === typeFilter;

      // Filtre par date
      const mouvementDate = new Date(mouvement.date_mouvement).toLocaleDateString('fr-FR');
      const matchesDate = dateFilter === 'TOUTES' || mouvementDate === aujourdhui;

      return matchesSearch && matchesType && matchesDate;
    });
  }, [safeMouvementsData, searchTerm, typeFilter, dateFilter]);

  // Données paginées
  const paginatedData = useMemo(() =>
    filteredData.slice(
      pagination.page * pagination.rowsPerPage,
      (pagination.page + 1) * pagination.rowsPerPage
    ),
    [filteredData, pagination.page, pagination.rowsPerPage]
  );

  // Gestionnaires d'événements
  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  const handleTypeFilterChange = useCallback((event) => {
    setTypeFilter(event.target.value);
    setPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  const handleDateFilterChange = useCallback((event) => {
    setDateFilter(event.target.value);
    setPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  const handlePageChange = useCallback((event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setPagination({
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10),
    });
  }, []);

  // Export PDF
  const handleExportPDF = useCallback(() => {
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString('fr-FR');

    // En-tête
    doc.setFontSize(20);
    doc.setTextColor(40, 40, 40);
    doc.text('Rapport des Mouvements de Stock', 20, 30);

    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(`Généré le ${today}`, 20, 40);
    doc.text(`Total des mouvements: ${stats.total}`, 20, 50);
    doc.text(`Entrées: ${stats.entrees} | Sorties: ${stats.sorties}`, 20, 60);

    // Tableau des données
    const tableData = filteredData.map(mouvement => [
      new Date(mouvement.date_mouvement).toLocaleDateString('fr-FR'),
      mouvement.type_mouvement,
      mouvement.nomProd,
      mouvement.quantite,
      mouvement.quantite_avant,
      mouvement.quantite_apres,
      mouvement.reference || '-',
      mouvement.motif || '-',
      mouvement.speudo || 'Système',
    ]);

    doc.autoTable({
      startY: 70,
      head: [['Date', 'Type', 'Produit', 'Quantité', 'Stock Avant', 'Stock Après', 'Référence', 'Motif', 'Utilisateur']],
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 7 },
      headStyles: { fillColor: [66, 66, 66] },
    });

    // Statistiques en bas
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.text(`Quantité totale traitée: ${stats.quantiteTotale} unités`, 20, finalY);

    doc.save(`mouvements-stock-detail-${today}.pdf`);
  }, [filteredData, stats]);

  // Obtenir la couleur selon le type de mouvement
  const getTypeColor = (type) => {
    const colors = {
      'ENTREE': '#4CAF50',
      'SORTIE_CAISSE': '#f44336',
      'SORTIE_CLIENT': '#2196F3',
      'DEFECT': '#9C27B0',
      'RETOUR': '#FF9800',
    };
    return colors[type] || '#757575';
  };

  // Obtenir l'icône selon le type
  const getTypeIcon = (type) => {
    const icons = {
      'ENTREE': '📥',
      'SORTIE_CAISSE': '💰',
      'SORTIE_CLIENT': '👤',
      'DEFECT': '⚠️',
      'RETOUR': '🔄',
    };
    return icons[type] || '📋';
  };

  if (loading && safeMouvementsData.length === 0) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={50} />
        <Typography variant="h6" color="textSecondary">
          Chargement des mouvements...
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* En-tête */}
      <Box className={classes.header}>
        <Typography variant="h4" gutterBottom color="primary">
          📈 Journal des Mouvements
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Suivi détaillé de tous les mouvements de stock avec analyse avancée
        </Typography>
      </Box>

      {/* Cartes de statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={`${classes.statCard} ${classes.statCardEntree}`}>
            <CardContent>
              <TrendingUp className={classes.statIcon} style={{ color: '#4CAF50' }} />
              <Typography className={classes.statNumber}>
                {stats.entrees}
              </Typography>
              <Typography className={classes.statLabel}>
                Entrées
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {stats.quantiteTotale > 0 ? 
                  `${Math.round((stats.entrees / stats.total) * 100)}% du total` : 
                  'Aucune donnée'
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={`${classes.statCard} ${classes.statCardSortie}`}>
            <CardContent>
              <TrendingDown className={classes.statIcon} style={{ color: '#f44336' }} />
              <Typography className={classes.statNumber}>
                {stats.sorties}
              </Typography>
              <Typography className={classes.statLabel}>
                Sorties
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {stats.quantiteTotale > 0 ? 
                  `${Math.round((stats.sorties / stats.total) * 100)}% du total` : 
                  'Aucune donnée'
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={`${classes.statCard} ${classes.statCardTotal}`}>
            <CardContent>
              <AllInclusive className={classes.statIcon} style={{ color: '#2196F3' }} />
              <Typography className={classes.statNumber}>
                {stats.total}
              </Typography>
              <Typography className={classes.statLabel}>
                Total Mouvements
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {stats.quantiteTotale} unités traitées
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={`${classes.statCard} ${classes.statCardTotal}`}>
            <CardContent>
              <BarChart className={classes.statIcon} style={{ color: '#FF9800' }} />
              <Typography className={classes.statNumber}>
                {Array.from(mouvementsParType.keys()).length}
              </Typography>
              <Typography className={classes.statLabel}>
                Types Différents
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Catégories de mouvements
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section de recherche et filtres */}
      <Card className={classes.searchSection}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              🔍 Filtres Avancés
            </Typography>
            <Box display="flex" gap={1}>
              <Tooltip title="Actualiser les données">
                <IconButton onClick={onRefresh} disabled={loading}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Button
                className={classes.exportButton}
                startIcon={<PictureAsPdf />}
                onClick={handleExportPDF}
                variant="contained"
                disabled={!filteredData.length}
              >
                Exporter PDF
              </Button>
            </Box>
          </Box>

          <div className={classes.filterRow}>
            <TextField
              placeholder="Rechercher produit, motif, référence, utilisateur..."
              value={searchTerm}
              onChange={handleSearchChange}
              variant="outlined"
              size="small"
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              style={{ minWidth: 320 }}
            />

            <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
              <InputLabel>Type de mouvement</InputLabel>
              <Select
                value={typeFilter}
                onChange={handleTypeFilterChange}
                label="Type de mouvement"
              >
                <MenuItem value="TOUS">Tous les types</MenuItem>
                <MenuItem value="ENTREE">Entrée</MenuItem>
                <MenuItem value="SORTIE_CAISSE">Sortie Caisse</MenuItem>
                <MenuItem value="SORTIE_CLIENT">Sortie Client</MenuItem>
                <MenuItem value="DEFECT">Défaut</MenuItem>
                <MenuItem value="RETOUR">Retour</MenuItem>
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
              <InputLabel>Période</InputLabel>
              <Select
                value={dateFilter}
                onChange={handleDateFilterChange}
                label="Période"
              >
                <MenuItem value="TOUTES">Toutes dates</MenuItem>
                <MenuItem value="AUJOURDHUI">Aujourd'hui</MenuItem>
              </Select>
            </FormControl>

            <Box flex={1} display="flex" justifyContent="flex-end" alignItems="center">
              <Typography variant="body2" color="textSecondary">
                {filteredData.length} mouvement(s) trouvé(s)
                {dateFilter === 'AUJOURDHUI' && (
                  <Chip
                    label="Aujourd'hui"
                    size="small"
                    className={classes.todayBadge}
                    icon={<Today />}
                  />
                )}
              </Typography>
            </Box>
          </div>

          {/* Statistiques par type */}
          {mouvementsParType.size > 0 && (
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              {Array.from(mouvementsParType.entries()).map(([type, typeStats]) => (
                <Chip
                  key={type}
                  label={`${getTypeIcon(type)} ${type}: ${typeStats.count} (${typeStats.quantite} unités)`}
                  size="small"
                  variant="outlined"
                  style={{
                    borderColor: getTypeColor(type),
                    color: getTypeColor(type),
                  }}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Tableau des mouvements */}
      <Card>
        <CardContent style={{ padding: 0 }}>
          <Box p={3}>
            <Typography variant="h6">
              📋 Détail des Mouvements
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell>Date & Heure</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Produit</TableCell>
                  <TableCell align="right">Quantité</TableCell>
                  <TableCell>Stock Avant</TableCell>
                  <TableCell>Stock Après</TableCell>
                  <TableCell>Référence</TableCell>
                  <TableCell>Motif</TableCell>
                  <TableCell>Utilisateur</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((mouvement, index) => (
                  <TableRow key={mouvement.id_mouvement || index} className={classes.tableRow}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {new Date(mouvement.date_mouvement).toLocaleDateString('fr-FR')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(mouvement.date_mouvement).toLocaleTimeString('fr-FR')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${getTypeIcon(mouvement.type_mouvement)} ${mouvement.type_mouvement}`}
                        className={classes.typeBadge}
                        style={{
                          backgroundColor: getTypeColor(mouvement.type_mouvement),
                          color: 'white',
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {mouvement.nomProd}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Code: {mouvement.codeIdProd}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        className={
                          mouvement.quantite > 0 ? classes.positive :
                          mouvement.quantite < 0 ? classes.negative : classes.neutral
                        }
                      >
                        {mouvement.quantite > 0 ? '+' : ''}{mouvement.quantite}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {mouvement.quantite_avant}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {mouvement.quantite_apres}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontSize: '12px' }} noWrap>
                        {mouvement.reference || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={mouvement.motif || 'Aucun motif'} arrow>
                        <Typography variant="body2" noWrap style={{ maxWidth: 150 }}>
                          {mouvement.motif || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={mouvement.speudo || 'Système'}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage="Lignes par page:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} sur ${count}`
            }
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default React.memo(MouvementsStock);